import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Add, Block, Cancel, Delete, Edit, Save } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TableContainer,
  Typography,
} from '@mui/material'
import Card from '@mui/material/Card'

import { BlueprintFamilyExampleImage, resolveExampleImageGameName } from '../../../api/blueprintFamilies/exampleImages'
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn, SortOrder } from '../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../components/GRTooltip/GRTooltip'
import { ImageCarouselModal } from '../../../components/ImageCarouselModal/ImageCarouselModal'
import { useCurrentUserLanguage } from '../../../features/account/hooks/useCurrentUserLanguage'
import { displaySnackBar } from '../../../features/snackbar'
import { htmlDecode } from '../../../helpers/dom'
import usePage from '../../../hooks/usePage'
import { useRequiredParams } from '../../../hooks/useRequiredParams'
import PageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { TranslationMap } from '../../../types/TranslationMap'
import {
  MAX_ACCEPTED_EVENT_TYPES,
  MarketArchetype,
  useCreateMarketArchetypeMutation,
  useDeleteMarketArchetypeMutation,
  useGetTrackedArchetypesQuery,
  useUpdateMarketArchetypeMutation,
} from '../../api/markets'
import { ActionButton } from '../../components/ActionButton/ActionButton'
import { BackButton } from '../../components/BackButton/BackButton'
import { BlueprintFamilyImagesEditor } from '../../components/BlueprintFamilyImagesEditor/BlueprintFamilyImagesEditor'
import { EventTypeSelect } from '../../components/EventTypeSelect/EventTypeSelect'
import FloatingToolbar from '../../components/FloatingToolbar/FloatingToolbar'
import { InputTranslator } from '../../components/InputTranslator/InputTranslator'
import { languageServiceNameResolver } from '../../components/MarketLevelBlueprintSearch/marketLevelBlueprintGrouping'
import {
  acceptedEventTypeIds,
  acceptedEventTypeNames,
  droppedEventTypesWithEvents,
  eventTypeUsage,
} from '../../features/live-events-tracking/helpers/acceptedEventTypes'
import { getValidationErrorMessage } from '../../helpers/apiValidationError'
import { InputTranslatorType } from '../../types/InputTranslatorType'

type MarketLevelBlueprintsEditPageProps = {
  children?: ReactNode
}

type MarketLevelBlueprintFormValues = {
  names: TranslationMap
  discontinued: boolean
  descriptions: TranslationMap
  marketArchetypeId?: string
  allowedEventTypeIds: string[]
  images: BlueprintFamilyExampleImage[]
}

const emptyFormValues = (): MarketLevelBlueprintFormValues => ({
  names: { en: '', ja: '', zh: '' },
  descriptions: { en: '', ja: '', zh: '' },
  discontinued: false,
  allowedEventTypeIds: [],
  images: [],
})

export const MarketLevelBlueprintsEditPage: FC<MarketLevelBlueprintsEditPageProps> = ({ children }) => {
  usePage(PageService.getInternalPageWithId('internal-market-level-blueprints-edit'))
  const navigate = useNavigate()
  const { t } = useTranslation()
  const containerRef = useRef(null)
  const { archetypeId } = useRequiredParams<{ archetypeId?: string }>()

  // get tracked archetypes
  const { data: trackedArchetypes, isLoading: isLoadingTrackedArchetypes } = useGetTrackedArchetypesQuery()
  const [createMarketArchetype, { isLoading: isCreating }] = useCreateMarketArchetypeMutation()
  const [deleteMarketArchetype, { isLoading: isDeleting }] = useDeleteMarketArchetypeMutation()
  const [updateMarketArchetype, { isLoading: isUpdating }] = useUpdateMarketArchetypeMutation()

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<MarketArchetype> | undefined>()

  // The blueprint whose example screenshots are open in the gallery, from the count in the table.
  // Lets an analyst check what a blueprint shows customers without opening it for editing.
  const [galleryArchetype, setGalleryArchetype] = useState<MarketArchetype | null>(null)
  const galleryImages = galleryArchetype?.glossaryImages || []
  const userLanguage = useCurrentUserLanguage()

  // The carousel stays mounted between openings, so each opening gets a fresh one that starts on the
  // first image instead of keeping a position the next blueprint may not have.
  const [galleryOpenCount, setGalleryOpenCount] = useState(0)
  const handleOpenGallery = useCallback((row: MarketArchetype) => {
    setGalleryOpenCount((count) => count + 1)
    setGalleryArchetype(row)
  }, [])
  const handleCloseGallery = useCallback(() => setGalleryArchetype(null), [])

  const handleRemoveArchetype = useCallback(
    async (row: MarketArchetype, rowIndex: number) => {
      setConfirmDialogData({
        title: t('internal-live-events:delete_archetype_confirm_dialog_title'),
        confirmText: t('internal-live-events:delete_archetype_confirm_dialog_confirm_text', { name: row.names.en }),
        actionText: t('internal-common:delete'),
        destructiveAction: true,
        data: row,
      })
      setConfirmDialogOpen(true)
    },
    [t]
  )
  const handleEditArchetype = useCallback(
    async (row: MarketArchetype, rowIndex: number) => {
      const selectedArchetypeId = row.id
      if (!selectedArchetypeId) {
        displaySnackBar({ message: t('internal-live-events:cannot_save_archetype_missing_id'), severity: 'error', open: true })
        return
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
      navigate(`/internal/live-events-tracking/market-level-blueprints/edit/${selectedArchetypeId}`)
    },
    [navigate, t]
  )

  const editedArchetype = useMemo(() => trackedArchetypes?.find((archetype) => archetype.id === archetypeId), [archetypeId, trackedArchetypes])

  /**
   * Default form values based on the current archetype being edited
   */
  const defaultValues = useMemo(
    () => ({
      names: editedArchetype?.names || { en: '', ja: '', zh: '' },
      descriptions: editedArchetype?.descriptions || { en: '', ja: '', zh: '' },
      discontinued: editedArchetype?.discontinued || false,
      allowedEventTypeIds: acceptedEventTypeIds(editedArchetype?.eventTypes),
      images: editedArchetype?.glossaryImages || [],
    }),
    [editedArchetype]
  )

  // How many active events use each type, accepted or not, so the selection says what is behind a
  // type as well as whether it is accepted.
  const eventCounts = useMemo(() => eventTypeUsage(editedArchetype), [editedArchetype])

  const form = useForm<MarketLevelBlueprintFormValues>({
    defaultValues: defaultValues,
  })

  /**
   * Custom props passed to the table component for action handlers
   */
  const customProps = useMemo(
    () => ({
      handleRemoveArchetype,
      handleEditArchetype,
      handleOpenGallery,
    }),
    [handleRemoveArchetype, handleEditArchetype, handleOpenGallery]
  )

  const tableColumns: GRTableColumn<MarketArchetype, typeof customProps>[] = useMemo(
    () => [
      {
        labelAccessor: () => t('internal-common:name'),
        accessor: ({ row }) => row.names.en,
        cellProps: { sx: { textAlign: 'left' } },
        headerCellProps: { sx: { textAlign: 'left' } },
        sortable: true,
        sortOrder: SortOrder.DESC,
      },
      {
        labelAccessor: () => <Trans i18nKey="internal-live-events:description" />,
        accessor: ({ row }) => {
          const descriptionHtml = row.descriptions?.en || ''
          if (!descriptionHtml) return ''

          const descriptionText = htmlDecode(descriptionHtml) || ''

          return (
            <GRTooltip content={<Box sx={{ maxWidth: 400 }} dangerouslySetInnerHTML={{ __html: descriptionHtml }} />} disableInteractive={false}>
              <Box
                sx={{
                  display: 'block',
                  width: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {descriptionText}
              </Box>
            </GRTooltip>
          )
        },
        cellProps: { sx: { textAlign: 'left' } },
        headerCellProps: { sx: { textAlign: 'left' } },
        sortable: true,
      },
      {
        labelAccessor: () => <Trans i18nKey="internal-live-events:blueprint_images_column" />,
        accessor: ({ row, customTableProps }) => {
          const count = row.glossaryImages?.length || 0
          if (count === 0) {
            return (
              <Typography variant="body2" color="text.disabled">
                0
              </Typography>
            )
          }
          return (
            <Button
              size="small"
              onClick={() => customTableProps?.handleOpenGallery(row)}
              aria-label={t('internal-live-events:blueprint_images_open_gallery', { total: count })}
            >
              {count}
            </Button>
          )
        },
        cellProps: { sx: { textAlign: 'center' } },
        headerCellProps: { sx: { textAlign: 'center', width: 90 } },
        sortable: true,
        sortAccessor: ({ row }) => row.glossaryImages?.length || 0,
      },
      {
        labelAccessor: () => <Trans i18nKey="internal-live-events:accepted_event_types_column" />,
        accessor: ({ row }) => {
          const names = acceptedEventTypeNames(row.eventTypes, (typeId) => languageServiceNameResolver.eventTypeName(typeId))
          const label =
            names.length === 0
              ? t('internal-live-events:accepted_event_types_unrestricted')
              : t('internal-live-events:accepted_event_types_list_label', { types: names.join(', ') })
          // Same number treatment as the image count. Hovering it names the types, since there is
          // nothing further to open.
          const count =
            names.length === 0 ? (
              <Typography variant="body2" color="text.disabled">
                0
              </Typography>
            ) : (
              <Button size="small" aria-label={label}>
                {names.length}
              </Button>
            )
          return (
            <GRTooltip
              content={
                names.length === 0 ? (
                  label
                ) : (
                  <Stack spacing={0.25}>
                    {names.map((name, index) => (
                      <span key={`${name}-${index}`}>{name}</span>
                    ))}
                  </Stack>
                )
              }
            >
              <Box component="span">{count}</Box>
            </GRTooltip>
          )
        },
        cellProps: { sx: { textAlign: 'center' } },
        headerCellProps: { sx: { textAlign: 'center', width: 110 } },
        sortable: true,
        sortAccessor: ({ row }) => row.eventTypes?.length || 0,
      },
      {
        labelAccessor: () => <Trans i18nKey="live-events:live_event_evolution_type_discontinued" />,
        accessor: ({ row }) => {
          return row.discontinued ? <Block color="error" /> : undefined
        },
        cellProps: { sx: { textAlign: 'center' } },
        headerCellProps: { sx: { textAlign: 'center', width: 90 } },
        sortable: true,
        sortAccessor: ({ row }) => (row.discontinued ? 1 : 0),
      },
      {
        labelAccessor: () => <Trans i18nKey="internal-live-events:actions" />,
        headerCellProps: { sx: { width: 120 } },
        accessor: ({ row, rowIndex, customTableProps }) => (
          <Stack direction="row" spacing={1} justifyContent="center">
            <ActionButton onClick={() => customTableProps?.handleEditArchetype(row, rowIndex)} disabled={isDeleting}>
              <Edit fontSize="small" />
            </ActionButton>
            <ActionButton color="warning" onClick={() => customTableProps?.handleRemoveArchetype(row, rowIndex)} disabled={isDeleting}>
              <Delete fontSize="small" />
            </ActionButton>
          </Stack>
        ),
      },
    ],
    [isDeleting, t]
  )

  const { control, getValues, reset, watch } = form

  // Watch the names field to validate English name requirement
  const watchedNames = watch('names')
  const watchedAcceptedEventTypeIds = watch('allowedEventTypeIds')

  /**
   * Validates that the English name is provided
   */
  const isEnglishNameValid = useMemo(() => {
    return watchedNames?.en && watchedNames.en.trim().length > 0
  }, [watchedNames?.en])

  // Narrowing the accepted types is allowed even when events already break the result: the BE only
  // never blocks such an event. Naming the types that are about to lose their place, with how many
  // events sit behind them, tells the analyst which events will start reporting as mismatches.
  const droppedTypesWithEvents = useMemo(
    () =>
      droppedEventTypesWithEvents(watchedAcceptedEventTypeIds || [], editedArchetype?.eventTypes).map((eventType) => ({
        ...eventType,
        name: languageServiceNameResolver.eventTypeName(eventType.typeId) || eventType.typeId,
      })),
    [watchedAcceptedEventTypeIds, editedArchetype?.eventTypes]
  )

  useEffect(() => {
    if (archetypeId) {
      reset(defaultValues)
    }
  }, [archetypeId, reset, defaultValues])

  const [columns, setColumns] = useState<GRTableColumn<MarketArchetype, typeof customProps>[]>(tableColumns)

  const handleColumnsUpdate = useCallback((updatedColumns: GRTableColumn<MarketArchetype, typeof customProps>[]) => {
    setColumns(updatedColumns)
  }, [])

  /**
   * Handles the creation of a new market level blueprint
   *
   * Creates the blueprint with the current form values. Resets the form on success.
   */
  const handleAddArchetype = async () => {
    const names = getValues('names')
    const discontinued = getValues('discontinued')
    const descriptions = getValues('descriptions')
    const images = getValues('images')
    const acceptedEventTypes = getValues('allowedEventTypeIds')

    try {
      await createMarketArchetype({
        names,
        descriptions,
        discontinued,
        glossaryImages: images,
        allowedEventTypeIds: acceptedEventTypes,
      }).unwrap()

      reset(emptyFormValues())
    } catch (error) {
      // Same rejections as on save: the message names the screenshot or the event type the BE refused.
      const reason = getValidationErrorMessage(error)
      displaySnackBar({
        message: reason
          ? `${t('internal-live-events:failed_to_create_live_event_archetype')} ${reason}`
          : t('internal-live-events:failed_to_create_live_event_archetype'),
        severity: 'error',
        open: true,
      })
    }
  }

  /**
   * Handles cancellation of the edit operation
   *
   * Resets the form and navigates back to the edit list
   */
  const handleCancel = () => {
    reset(emptyFormValues())
    navigate(`/internal/live-events-tracking/market-level-blueprints/edit`)
  }

  /**
   * Handles saving changes to an existing market level blueprint
   *
   * Validates that an archetype ID is available, then updates the blueprint
   * with the current form values. Shows success/error messages and navigates
   * back to the edit list on success.
   */
  const handleSaveArchetype = async () => {
    const names = getValues('names')
    const descriptions = getValues('descriptions')
    const discontinued = getValues('discontinued')
    const images = getValues('images')
    const acceptedEventTypes = getValues('allowedEventTypeIds')

    if (!archetypeId) {
      displaySnackBar({ message: t('internal-live-events:cannot_save_archetype_missing_id'), severity: 'error', open: true })
      return
    }

    try {
      await updateMarketArchetype({
        archetypeId,
        names,
        descriptions,
        discontinued,
        glossaryImages: images,
        allowedEventTypeIds: acceptedEventTypes,
      }).unwrap()

      displaySnackBar({ message: t('internal-live-events:archetype_updated_successfully'), severity: 'success', open: true })
      reset(emptyFormValues())
      navigate(`/internal/live-events-tracking/market-level-blueprints/edit`)
    } catch (error) {
      // A rejection here names the offending value ("screenshot '…' not found on event '…'", or an
      // event type that does not exist), which is what an analyst needs to fix the form, so it rides
      // along with the generic copy.
      const reason = getValidationErrorMessage(error)
      displaySnackBar({
        message: reason ? `${t('internal-live-events:failed_to_update_archetype')} ${reason}` : t('internal-live-events:failed_to_update_archetype'),
        severity: 'error',
        open: true,
      })
    }
  }

  /**
   * Executes the deletion of a market level blueprint after confirmation
   *
   * @param confirmData - Confirmation dialog data containing the blueprint to delete
   */
  const handleConfirmDelete = async (confirmData?: ConfirmDialogData<MarketArchetype>) => {
    const archetypeId = confirmData?.data ? confirmData.data.id : undefined

    if (!archetypeId) {
      displaySnackBar({ message: t('internal-live-events:cannot_delete_archetype_missing_id'), severity: 'error', open: true })
      return
    }

    try {
      await deleteMarketArchetype(archetypeId).unwrap()
      displaySnackBar({ message: t('internal-live-events:archetype_deleted_successfully'), severity: 'success', open: true })
    } catch (error) {
      displaySnackBar({ message: t('internal-live-events:failed_to_delete_archetype'), severity: 'error', open: true })
    }
  }

  /**
   * Handles the closing of the confirmation dialog
   *
   * @param confirmData - Confirmation dialog data if user confirmed the action
   */
  const handleConfirmDialogClose = (confirmData?: ConfirmDialogData<MarketArchetype>) => {
    setConfirmDialogOpen(false)
    if (confirmData) {
      handleConfirmDelete(confirmData)
    }
  }

  return (
    <>
      <FormProvider {...form}>
        <Card sx={{ marginTop: 3 }}>
          <CardHeader title={archetypeId ? t('internal-live-events:edit_market_level_blueprints') : t('internal-live-events:add_new_market_level_blueprint')} />
          <Divider />
          <CardContent>
            <Grid container spacing={2} flexWrap="wrap">
              <Grid xs={12} item>
                <Controller
                  name="names"
                  control={control}
                  render={({ field }) => (
                    <InputTranslator
                      value={field.value}
                      onChange={field.onChange}
                      label={t('internal-common:name')}
                      dialogTitle={t('internal-live-events:market_level_blueprint_name')}
                      inputType={InputTranslatorType.Text}
                    />
                  )}
                />
              </Grid>

              <Grid xs={12} item>
                <Controller
                  name="descriptions"
                  control={control}
                  render={({ field }) => (
                    <InputTranslator
                      label={t('internal-live-events:description')}
                      value={field.value}
                      inputType={InputTranslatorType.WYSIWYG}
                      dialogTitle={t('internal-live-events:description')}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid>

              <Grid xs={12} item>
                <Controller
                  name="allowedEventTypeIds"
                  control={control}
                  render={({ field }) => (
                    <EventTypeSelect
                      value={field.value}
                      onChange={field.onChange}
                      label={t('internal-live-events:allowed_event_types')}
                      placeholder={t('internal-live-events:allowed_event_types_placeholder')}
                      maxSelected={MAX_ACCEPTED_EVENT_TYPES}
                      helperText={
                        field.value.length >= MAX_ACCEPTED_EVENT_TYPES
                          ? t('internal-live-events:allowed_event_types_max_reached', { max: MAX_ACCEPTED_EVENT_TYPES })
                          : field.value.length === 0
                          ? t('internal-live-events:allowed_event_types_helper_unrestricted', { max: MAX_ACCEPTED_EVENT_TYPES })
                          : t('internal-live-events:allowed_event_types_helper', { max: MAX_ACCEPTED_EVENT_TYPES })
                      }
                      eventCounts={eventCounts}
                    />
                  )}
                />
              </Grid>

              {droppedTypesWithEvents.length > 0 && (
                <Grid xs={12} item>
                  <Alert severity="warning">
                    {t('internal-live-events:allowed_event_types_dropped_warning', {
                      types: droppedTypesWithEvents.map((eventType) => `${eventType.name} (${eventType.count})`).join(', '),
                    })}
                  </Alert>
                </Grid>
              )}

              <Grid xs={2} item sx={{ display: 'flex', justifyContent: 'center' }}>
                <Controller
                  name="discontinued"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                      label={t('live-events:live_event_evolution_type_discontinued')}
                    />
                  )}
                />
              </Grid>

              {/* Available on create as well: screenshots are references to events, not uploads, so
                  nothing here needs the blueprint's id. */}
              <Grid xs={12} item>
                <Controller
                  name="images"
                  control={control}
                  render={({ field }) => <BlueprintFamilyImagesEditor images={field.value || []} onChange={field.onChange} />}
                />
              </Grid>
            </Grid>
          </CardContent>
          <Divider />
          <CardActions sx={{ justifyContent: 'flex-end', padding: 1 }}>
            {!archetypeId ? (
              <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleAddArchetype} disabled={!isEnglishNameValid || isCreating}>
                {isCreating ? t('internal-live-events:adding') : t('internal-live-events:add')}
              </Button>
            ) : (
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" color="primary" startIcon={<Cancel />} onClick={handleCancel} disabled={isUpdating}>
                  {t('internal-common:cancel')}
                </Button>
                <Button variant="contained" color="primary" startIcon={<Save />} onClick={handleSaveArchetype} disabled={!isEnglishNameValid || isUpdating}>
                  {isUpdating ? t('internal-live-events:saving') : t('internal-common:save')}
                </Button>
              </Stack>
            )}
          </CardActions>
        </Card>
      </FormProvider>

      <Card sx={{ marginTop: 3 }}>
        <CardHeader title={t('internal-live-events:market_level_blueprints')} />
        <Divider />
        <CardContent sx={{ padding: 0, '&:last-child': { paddingBottom: 0 } }}>
          <TableContainer component={Box} ref={containerRef}>
            <GRTable
              rows={trackedArchetypes || []}
              columns={columns}
              rowIdKey={(row) => row.id || ''}
              scroller={containerRef}
              noRowsLabel={t('internal-live-events:no_market_level_blueprints_found')}
              isLoading={isLoadingTrackedArchetypes}
              onColumnsUpdated={handleColumnsUpdate}
              gridlines={true}
              striped
              hoverable
              customProps={customProps}
            />
          </TableContainer>
        </CardContent>
      </Card>

      <FloatingToolbar>
        <BackButton mb={0} url="/internal/live-events-tracking" />
      </FloatingToolbar>

      <ConfirmDialog open={confirmDialogOpen} onClose={handleConfirmDialogClose} confirmDialogData={confirmDialogData} />
      <ImageCarouselModal
        key={galleryOpenCount}
        open={galleryArchetype !== null}
        onClose={handleCloseGallery}
        imageUrls={galleryImages.map((image) => image.url)}
        title={galleryArchetype?.names.en}
        renderCaption={(index) => {
          const image = galleryImages[index]
          if (!image) return null
          // Same attribution the customer sees under this shot, so the analyst can tell at a glance
          // which game and event a blueprint is illustrated by.
          const gameName = resolveExampleImageGameName(image, userLanguage)
          return (
            <Typography variant="body2" color="text.secondary">
              {gameName ? `${gameName} · ${image.eventName}` : image.eventName}
            </Typography>
          )
        }}
      />
    </>
  )
}
